import React, { useState } from 'react';
import { ChevronLeft, ChevronRight, Maximize2 } from 'lucide-react';

/**
 * Product screenshot gallery — thumbnail row + full-width active shot.
 * Each shot has an optional caption. Keeps the "actual product" feel the
 * brief calls for, without hidden slideshows or heavy image assets.
 */
const ScreenshotGallery = ({ shots = [], eyebrow = 'Inside the platform', title, description, testid }) => {
  const [active, setActive] = useState(0);
  const [zoom, setZoom] = useState(false);

  if (!shots.length) return null;
  const current = shots[active];
  const next = () => setActive((i) => (i + 1) % shots.length);
  const prev = () => setActive((i) => (i - 1 + shots.length) % shots.length);

  return (
    <section className="py-16 md:py-20 bg-slate-950 text-white" data-testid={testid || 'screenshot-gallery'}>
      <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
        <p className="text-xs uppercase tracking-[0.22em] text-blue-400 font-semibold mb-4">{eyebrow}</p>
        {title && <h2 className="text-3xl md:text-4xl font-medium tracking-tight leading-tight">{title}</h2>}
        {description && <p className="mt-3 text-slate-400 max-w-3xl">{description}</p>}

        <div className="mt-10 relative bg-slate-900 rounded-2xl p-2 shadow-[0_20px_60px_-20px_rgba(0,0,0,0.6)] ring-1 ring-white/5">
          <img
            src={current.src}
            alt={current.alt || current.caption || 'Product screenshot'}
            loading="lazy"
            className="w-full aspect-[16/9] object-cover object-top rounded-lg cursor-zoom-in"
            onClick={() => setZoom(true)}
          />
          <div className="absolute inset-x-0 bottom-2 flex items-center justify-between px-3">
            <button onClick={prev} className="w-8 h-8 rounded-full bg-slate-900/70 backdrop-blur border border-white/10 flex items-center justify-center hover:bg-slate-800" data-testid="gallery-prev" aria-label="Previous">
              <ChevronLeft className="w-4 h-4" />
            </button>
            <button onClick={() => setZoom(true)} className="text-xs bg-slate-900/70 backdrop-blur border border-white/10 rounded-full px-3 py-1.5 hover:bg-slate-800 inline-flex items-center gap-1.5">
              <Maximize2 className="w-3 h-3" /> View full
            </button>
            <button onClick={next} className="w-8 h-8 rounded-full bg-slate-900/70 backdrop-blur border border-white/10 flex items-center justify-center hover:bg-slate-800" data-testid="gallery-next" aria-label="Next">
              <ChevronRight className="w-4 h-4" />
            </button>
          </div>
        </div>

        {current.caption && (
          <p className="mt-4 text-sm text-slate-400" data-testid="gallery-caption">{current.caption}</p>
        )}

        <div className="mt-6 grid grid-cols-3 sm:grid-cols-5 md:grid-cols-6 gap-2">
          {shots.map((s, i) => (
            <button
              key={s.src}
              onClick={() => setActive(i)}
              className={`aspect-video rounded-md overflow-hidden border-2 transition-all ${i === active ? 'border-blue-400' : 'border-transparent opacity-60 hover:opacity-100'}`}
              data-testid={`gallery-thumb-${i}`}
              aria-label={`View screenshot ${i + 1}`}
            >
              <img src={s.src} alt={s.alt || ''} loading="lazy" className="w-full h-full object-cover object-top" />
            </button>
          ))}
        </div>
      </div>
      {zoom && (
        <div className="fixed inset-0 z-50 bg-black/90 flex items-center justify-center p-4 cursor-zoom-out" onClick={() => setZoom(false)} data-testid="gallery-lightbox">
          <img src={current.src} alt={current.alt || ''} className="max-w-full max-h-full object-contain rounded-lg" />
        </div>
      )}
    </section>
  );
};

export default ScreenshotGallery;
